import { useState, useEffect, useMemo } from 'react';
import {
  ChevronRight, ChevronDown, RefreshCw, AlertTriangle, AlertOctagon,
  Folder, User as UserIcon, Building2, LogIn
} from 'lucide-react';
import { apiClient } from '../../lib/api';

async function impersonateUser(userId: string, email: string) {
  if (!window.confirm(`Impersonate ${email}?`)) return;
  try {
    const response = await apiClient.startImpersonation(userId, 'Catalog hierarchy cleanup');
    if (!response.success || !response.data) {
      alert(response.error || 'Failed to start impersonation');
      return;
    }
    const currentToken = localStorage.getItem('bloom_token');
    if (currentToken) localStorage.setItem('bloom_admin_token', currentToken);
    localStorage.setItem('bloom_token', response.data.token);
    localStorage.removeItem('bloom-auth');
    sessionStorage.clear();
    window.location.replace('/dashboard');
  } catch (e: any) {
    alert(e?.message || 'Failed to start impersonation');
  }
}

type IssueOrphaned = {
  type: 'orphaned_products';
  count: number;
  samples: { productId: string; name: string; ownPrice: number }[];
};
type IssueBelowInherit = {
  type: 'below_inheritance';
  count: number;
  samples: { productId: string; name: string; ownPrice: number; upstreamPrice: number }[];
};
type IssueBelowBase = {
  type: 'below_base_price';
  count: number;
  samples: { productId: string; name: string; ownPrice: number; basePrice: number }[];
};
type IssueAdminToAmWithSv = {
  type: 'admin_to_am_with_supervisor';
  catalogs: { id: string; name: string }[];
};
type IssueNoUpstream = {
  type: 'no_upstream';
  ownedCatalogCount: number;
};
type Issue =
  | IssueOrphaned | IssueBelowInherit | IssueBelowBase
  | IssueAdminToAmWithSv | IssueNoUpstream;

interface Node {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'supervisor' | 'distributor' | 'physician';
  received: { catalogCount: number; productCount: number };
  given: { catalogCount: number; productCount: number; assigneeCount: number };
  issues: Issue[];
  children?: Node[];
}

interface AuditResponse {
  success: boolean;
  data: {
    threeTier: Node[];
    twoTier: Node[];
    summary: {
      supervisorCount: number;
      twoTierAmCount: number;
      threeTierAmCount: number;
      productCount: number;
      activeCatalogCount: number;
    };
  };
}

const ROLE_ICON: Record<Node['role'], typeof Folder> = {
  supervisor: Building2,
  distributor: Folder,
  physician: UserIcon,
};

const ROLE_LABEL: Record<Node['role'], string> = {
  supervisor: 'Supervisor',
  distributor: 'AM',
  physician: 'Physician',
};

function IssueBadge({ issue }: { issue: Issue }) {
  const cfg = {
    orphaned_products: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Orphaned' },
    below_inheritance: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Below upstream' },
    below_base_price: { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Below base' },
    admin_to_am_with_supervisor: { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Admin→AM(sv)' },
    no_upstream: { color: 'bg-red-100 text-red-800 border-red-200', label: 'No upstream' },
  }[issue.type];

  const count =
    issue.type === 'orphaned_products' ? issue.count :
    issue.type === 'below_inheritance' ? issue.count :
    issue.type === 'below_base_price' ? issue.count :
    issue.type === 'admin_to_am_with_supervisor' ? issue.catalogs.length :
    issue.ownedCatalogCount;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cfg.color}`}>
      <AlertTriangle className="w-3 h-3" />
      {cfg.label} ({count})
    </span>
  );
}

function IssueDetail({ issue }: { issue: Issue }) {
  if (issue.type === 'no_upstream') {
    return (
      <div className="text-sm text-gray-700">
        Owns {issue.ownedCatalogCount} catalog(s) with no upstream coverage. Every product priced
        against <code>basePrice</code> fallback.
      </div>
    );
  }
  if (issue.type === 'admin_to_am_with_supervisor') {
    return (
      <div className="text-sm text-gray-700">
        Admin catalogs assigned directly (silently ignored by pricing service):
        <ul className="list-disc ml-5 mt-1">
          {issue.catalogs.map(c => <li key={c.id}>{c.name}</li>)}
        </ul>
      </div>
    );
  }
  const rows =
    issue.type === 'orphaned_products' ? issue.samples.map(s => ({
      name: s.name, own: s.ownPrice, ref: null, refLabel: ''
    })) :
    issue.type === 'below_inheritance' ? issue.samples.map(s => ({
      name: s.name, own: s.ownPrice, ref: s.upstreamPrice, refLabel: 'upstream'
    })) :
    issue.samples.map(s => ({
      name: s.name, own: s.ownPrice, ref: s.basePrice, refLabel: 'base'
    }));

  return (
    <div className="text-sm">
      <table className="w-full">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b">
            <th className="py-1 pr-2">Product</th>
            <th className="py-1 px-2 text-right">Own price</th>
            {rows[0]?.refLabel && <th className="py-1 px-2 text-right">{rows[0].refLabel}</th>}
            {rows[0]?.refLabel && <th className="py-1 px-2 text-right">Δ</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b last:border-b-0">
              <td className="py-1 pr-2">{r.name}</td>
              <td className="py-1 px-2 text-right">${r.own.toFixed(2)}</td>
              {r.ref !== null && <td className="py-1 px-2 text-right">${r.ref.toFixed(2)}</td>}
              {r.ref !== null && (
                <td className={`py-1 px-2 text-right font-mono ${r.own < r.ref ? 'text-red-600' : ''}`}>
                  {r.own < r.ref ? '−' : '+'}${Math.abs(r.own - r.ref).toFixed(2)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {'count' in issue && issue.samples.length < issue.count && (
        <div className="text-xs text-gray-500 mt-1">
          showing {issue.samples.length} of {issue.count}
        </div>
      )}
    </div>
  );
}

function nodeIsVisible(node: Node, hideHealthy: boolean): boolean {
  if (!hideHealthy) return true;
  if (node.issues.length > 0) return true;
  return (node.children ?? []).some(c => nodeIsVisible(c, hideHealthy));
}

function TreeNode({
  node,
  depth,
  hideHealthy,
}: {
  node: Node;
  depth: number;
  hideHealthy: boolean;
}) {
  const visibleChildren = useMemo(
    () => (node.children ?? []).filter(c => nodeIsVisible(c, hideHealthy)),
    [node.children, hideHealthy]
  );
  const descendantIssueCount = useMemo(() => {
    const count = (n: Node): number =>
      n.issues.length + (n.children?.reduce((s, c) => s + count(c), 0) ?? 0);
    return count(node) - node.issues.length;
  }, [node]);

  const [expanded, setExpanded] = useState(depth === 0 && visibleChildren.length > 0);
  const [issueExpanded, setIssueExpanded] = useState<number | null>(null);

  if (hideHealthy && node.issues.length === 0 && visibleChildren.length === 0) return null;

  const Icon = ROLE_ICON[node.role];
  const hasVisibleChildren = visibleChildren.length > 0;
  const name = [node.firstName, node.lastName].filter(Boolean).join(' ') || node.email;

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-1.5 hover:bg-gray-50 rounded ${hasVisibleChildren ? 'cursor-pointer' : ''}`}
        style={{ paddingLeft: `${depth * 20 + 4}px` }}
        onClick={() => {
          if (hasVisibleChildren) setExpanded(v => !v);
        }}
      >
        {hasVisibleChildren ? (
          expanded
            ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}
        <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="text-xs font-medium text-gray-400 uppercase w-16 flex-shrink-0">
          {ROLE_LABEL[node.role]}
        </span>
        <span className="font-medium text-gray-900 truncate">{name}</span>
        <span className="text-xs text-gray-500 truncate">{node.email}</span>
        <div className="ml-auto flex items-center gap-3 text-xs text-gray-600 flex-shrink-0">
          <span title="Catalogs received / products received">
            ← {node.received.catalogCount} cat / {node.received.productCount} prod
          </span>
          <span title="Catalogs given / products given / assignees">
            → {node.given.catalogCount} cat / {node.given.productCount} prod / {node.given.assigneeCount} assignees
          </span>
          {node.issues.map((issue, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setIssueExpanded(issueExpanded === i ? null : i); }}
            >
              <IssueBadge issue={issue} />
            </button>
          ))}
          {descendantIssueCount > 0 && (
            <span className="text-xs text-gray-500" title="Issues in descendants">
              +{descendantIssueCount} below
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); void impersonateUser(node._id, node.email); }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
            title={`Impersonate ${node.email}`}
          >
            <LogIn className="w-3 h-3" />
            Impersonate
          </button>
        </div>
      </div>

      {issueExpanded !== null && node.issues[issueExpanded] && (
        <div
          className="my-1 p-3 bg-gray-50 rounded border border-gray-200"
          style={{ marginLeft: `${depth * 20 + 32}px` }}
        >
          <IssueDetail issue={node.issues[issueExpanded]} />
        </div>
      )}

      {expanded && hasVisibleChildren && (
        <div>
          {visibleChildren.map(child => (
            <TreeNode key={child._id} node={child} depth={depth + 1} hideHealthy={hideHealthy} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CatalogHierarchy() {
  const [data, setData] = useState<AuditResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hideHealthy, setHideHealthy] = useState(true);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<AuditResponse>('/admin/catalog-hierarchy-audit');
      if (!res.success) throw new Error('Audit failed');
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const totals = useMemo(() => {
    if (!data) return null;
    const countIssues = (nodes: Node[]): { nodes: number; issues: number } => {
      let nodeCount = 0, issueCount = 0;
      const walk = (n: Node) => {
        nodeCount++;
        issueCount += n.issues.length;
        n.children?.forEach(walk);
      };
      nodes.forEach(walk);
      return { nodes: nodeCount, issues: issueCount };
    };
    const three = countIssues(data.threeTier);
    const two = countIssues(data.twoTier);
    return {
      totalNodes: three.nodes + two.nodes,
      totalIssues: three.issues + two.issues,
    };
  }, [data]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Catalog Hierarchy Audit</h1>
          <p className="text-sm text-gray-500 mt-1">
            Supervisor / AM / physician tree with upstream vs downstream catalog counts and discrepancy flags.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={hideHealthy}
              onChange={e => setHideHealthy(e.target.checked)}
            />
            Hide healthy
          </label>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
          <AlertOctagon className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-5 gap-4 mb-6">
            <SummaryCard label="Supervisors" value={data.summary.supervisorCount} />
            <SummaryCard label="AMs (3-tier)" value={data.summary.threeTierAmCount} />
            <SummaryCard label="AMs (2-tier)" value={data.summary.twoTierAmCount} />
            <SummaryCard label="Active catalogs" value={data.summary.activeCatalogCount} />
            <SummaryCard
              label="Flagged nodes"
              value={totals?.totalIssues ?? 0}
              highlight={totals && totals.totalIssues > 0}
            />
          </div>

          <div className="bg-white border rounded-lg p-4 mb-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              3-tier chains (Supervisor → AM → Physician)
            </h2>
            {data.threeTier.map(n => (
              <TreeNode key={n._id} node={n} depth={0} hideHealthy={hideHealthy} />
            ))}
            {data.threeTier.length === 0 && (
              <div className="text-sm text-gray-500 py-4">No supervisors configured.</div>
            )}
          </div>

          <div className="bg-white border rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              2-tier chains (AM → Physician)
            </h2>
            {data.twoTier.map(n => (
              <TreeNode key={n._id} node={n} depth={0} hideHealthy={hideHealthy} />
            ))}
            {data.twoTier.length === 0 && (
              <div className="text-sm text-gray-500 py-4">No 2-tier AMs.</div>
            )}
          </div>
        </>
      )}

      {loading && !data && (
        <div className="text-sm text-gray-500 py-8 text-center">
          <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
          Computing audit...
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, highlight }: { label: string; value: number; highlight?: boolean | null }) {
  return (
    <div className={`bg-white border rounded-lg p-4 ${highlight ? 'border-red-300' : ''}`}>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${highlight ? 'text-red-600' : 'text-gray-900'}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}
